import type { JSONValue, JSONObject } from 'lib/types';
import type { ReconcileAuditDetails } from 'types/api-responses';

export type ActivityType = 'version' | 'event';

/**
 * PRD action-type chip (e.g. Confirmed/green, Promoted/magenta) decorated
 * server-side by feeds that classify their rows (interest certification).
 * `color` is a Carbon Tag type.
 */
export interface ActionTag {
  label: string;
  color: 'green' | 'purple' | 'cyan' | 'blue' | 'magenta';
}

export interface ActivityFeedItemBase {
  activityId: string;
  activityType: ActivityType;
  id: number;
  createdAt: string;
  itemType: string;
  itemId: number;
  actionTag?: ActionTag;
  /** Server-decorated slot/well context (interest certification feeds). */
  slotName?: string | null;
  /** Server-rendered card sentence; rows without it render raw. */
  summary?: string;
  /** Card footer-left: user name, or the mechanism (e.g. "Auto-Confirm (1%)"). */
  actor?: string;
  /** Reconcile/undo rows: action label + structured detail bullets. */
  details?: ReconcileAuditDetails;
  /** Server-injected link to the item's detail page (eval3-new URL). */
  itemHref?: string;
}

export interface VersionActivityItem extends ActivityFeedItemBase {
  activityType: 'version';
  data: {
    event: 'create' | 'update' | 'destroy';
    whodunnit: string | null;
    updatedAt: string;
    objectChanges: Record<string, [JSONValue, JSONValue]>;
  };
}

export interface EventActivityItem extends ActivityFeedItemBase {
  activityType: 'event';
  data: {
    kind: string;
    userId: number | null;
    createdBy: string;
    updatedAt: string;
    payload: JSONObject;
  };
}

export type ActivityFeedItem = VersionActivityItem | EventActivityItem;

export const isVersionActivity = (
  item: ActivityFeedItem
): item is VersionActivityItem => item.activityType === 'version';

export const isEventActivity = (
  item: ActivityFeedItem
): item is EventActivityItem => item.activityType === 'event';
